import React, { useCallback, useEffect, useState } from "react";
import styled from 'styled-components';
import BlogV2 from "../indexV2";
import Row from "../../Bootstrap/Row";
import { fetchJSON } from "../../../utilities/fetch";
import Loader from "../../Loader";
import { Content } from '../../../../';
import { useIsMobile } from "../../../context/MobileContext"; 

const MainContainer = styled.div`
    width: 100%;
    overflow-x: hidden; /* Prevent horizontal scrolling */
    
    /* Ensure all child elements respect container width */
    & > * {
        max-width: 100%;
    }

    @media (max-width: 991px) {
        margin-bottom: 0 !important;
    }
`;

const TopFeaturedSection = styled.div`
    margin-bottom: 3rem;

    @media (max-width: 991px) {
        margin-bottom: 0 !important;
    }
`;

const RightColumnContainer = styled.div`
    /* On large screens, keep stacked layout */
    @media (min-width: 992px) {
        padding-left: 3rem;
        
        & > div {
        margin-bottom: 1.5rem;
        }
    }
    
    /* On smaller screens, use grid layout */
    @media (max-width: 991px) {
        display: flex;
        flex-wrap: wrap;
        
        & > div {
            width: 50%;
            padding: 0 15px;
        }
    }
`;

// Custom styled component for the grid to maintain two columns on mobile
const GridRow = styled(Row)`
    /* Override Bootstrap's default behavior for xs screens */
    .col-xs-12 {
        @media (max-width: 991px) {
        flex: 0 0 50% !important;
        max-width: 50% !important;
        }
    }

    /* Desktop view (3 columns) with equal visual width */
    @media (min-width: 992px) {
        .blog-results-item {
            /* Remove default padding first */
            padding: 0;
            
            /* First item in each row */
            &:nth-child(3n+1) {
                padding-right: 20px; /* Increased right padding */
                padding-left: 0;
            }
            
            /* Last item in each row */
            &:nth-child(3n) {
                padding-left: 20px; /* Increased left padding */
                padding-right: 0;
            }
            
            /* Middle item in each row */
            &:nth-child(3n+2) {
                padding-left: 10px; /* Half of 2.74rem */
                padding-right: 10px; /* Half of 2.74rem */
            }
        }
    }

    @media (max-width: 991px) {
        .blog-results-item {
            margin-bottom: .5rem !important;
            
            /* Fix grid padding on mobile */
            &:nth-child(odd) {
                padding-left: 0 !important;
            }
            
            &:nth-child(even) {
                padding-right: 0 !important;
            }
        }
    }
`;

interface IBlogResultsProps {
    category?: string;
    limit: number;
    query?: string;
    tag?: string;
    onCategoryChange?: (category: string) => void;
}

export function BlogResults(props: IBlogResultsProps) {
    const [topPost, setTopPost] = useState<any>(null);
    const [rightColumnPosts, setRightColumnPosts] = useState<any[]>([]);
    const [gridPosts, setGridPosts] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [isSearching, setIsSearching] = useState(false);
    const [lastPage, setLastPage] = useState(false);

    const useFeaturedLayout = !props.category || props.category === "All";
    
    const handleCategoryClick = (category: string) => {
        if (props.onCategoryChange) {
            props.onCategoryChange(category);
        }
    };

    const loadMore = useCallback(() => {
        setPage(page + 1);
    }, [page]);

    useEffect(() => {
        if (isSearching) return; // Prevent concurrent requests
        
        try {
            setIsSearching(true);
            const params = [];
            params.push(`q=${props.query || ''}`); // q is required even if blank
            params.push('pt=blog');
            params.push(`page=${page}`);
            params.push(`limit=${props.limit}`);

            if (props.category && !/all/gi.test(props.category)) {
                params.push(`cat=${props.category}`);
            }

            if (props.tag) {
                params.push(`tag=${props.tag}`);
            }
            const cacheBypassParam = `clientCacheBypass=${Math.floor(Date.now() / parseInt(process.env.SOLR_CACHE_INTERVAL_MS || '900001'))}`; // create a param to bypass the cache every 15 minutes
            params.push(cacheBypassParam);

            fetchJSON<any[any]>(`${process.env.GATSBY_SOLR_SEARCH_URL}?${params.join('&')}`)
                .then((res) => {
                    if (res.response.start + props.limit >= res.response.numFound) {
                        setLastPage(true);
                    }
                    
                    if (res.response.docs.length > 0) {
                        const allPosts = [...res.response.docs];
                        
                        if (page === 1) {
                            if (useFeaturedLayout) {
                                // Set the most recent post as the top featured post
                                if (allPosts.length > 0) {
                                    setTopPost(allPosts.shift());
                                }
                                
                                // Get the next two posts for the right column
                                if (allPosts.length > 0) {
                                    setRightColumnPosts(allPosts.splice(0, 2));
                                }
                                
                                // Remaining posts go into the grid
                                setGridPosts(allPosts);
                            } else {
                                // For other categories, all posts go into the grid
                                setTopPost(null);
                                setRightColumnPosts([]);
                                setGridPosts(allPosts);
                            }
                        } else {
                            // For subsequent pages, just add to the grid
                            setGridPosts(prevPosts => [...prevPosts, ...allPosts]);
                        }
                    } else {
                        // No posts found, reset layout
                        if (page === 1) {
                            setTopPost(null);
                            setRightColumnPosts([]);
                            setGridPosts([]);
                        }
                    }
                    setIsSearching(false);
                })
                .catch(err => {
                    console.error(err);
                    setIsSearching(false);
                });
        } catch (err) {
            console.error(err);
            setIsSearching(false);
        }
    }, [page, props.category, props.limit, props.query, props.tag, useFeaturedLayout]);

    const getCategories = (post) => {
        return post.category || [];
    };

    return (
        <MainContainer>
            {/* Only show the featured layout for "All" category */}
            {useFeaturedLayout && (
                <div className="px-xl-6 px-lg-5 px-0 no-border">
                    <GridRow>
                        {/* Left side - Top featured post */}
                        {topPost && (
                            <div className="col-lg-9 col-lg">
                                <TopFeaturedSection>
                                    <BlogV2
                                        headerAs="big"
                                        href={topPost.path?.[0]}
                                        title={topPost.blog_title?.[0]}
                                        timestamp={topPost.timestamp}
                                        tags={(topPost.tags || []).join(',')}
                                        alt={topPost.thumb_image_alt?.[0]}
                                        src={topPost.thumb_image_src?.[0]}
                                        dateType="timeAgo"
                                        isMainArticle={true}
                                        imageSize="featured"
                                        description={topPost.description?.[0]}
                                        categories={getCategories(topPost)}
                                        className="mb-5"
                                        onCategoryClick={handleCategoryClick}
                                    />
                                </TopFeaturedSection>
                            </div>
                        )}

                        {/* Right side - 2 stacked posts */}
                        {rightColumnPosts.length > 0 && (
                            <div className="col-lg-3">
                                <RightColumnContainer>
                                    {rightColumnPosts.map((post, index) => (
                                        <div className={`${index === 0 ? 'pl-0' : 'pr-0'}`} key={`right-col-${post.id || index}`}>
                                            <BlogV2
                                                headerAs="small"
                                                href={post.path?.[0]}
                                                title={post.blog_title?.[0]}
                                                timestamp={post.timestamp}
                                                tags={(post.tags || []).join(',')}
                                                alt={post.thumb_image_alt?.[0]}
                                                src={post.thumb_image_src?.[0]}
                                                dateType="date"
                                                isMainArticle={false}
                                                imageSize="secondary"
                                                categories={getCategories(post)}
                                                onCategoryClick={handleCategoryClick}
                                            />
                                        </div>
                                    ))}
                                </RightColumnContainer>
                            </div>
                        )}
                    </GridRow>
                </div>
            )}

            {/* Grid layout for remaining posts or all posts for non-"All" categories */}
            <Content className="px-xl-6 px-lg-5 px-0 no-border">
                <GridRow>
                    {gridPosts.length > 0 ? (
                        gridPosts.map((post, index) => (
                            <div 
                                data-test-id="blog-results-item" 
                                key={`grid-${post.id || index}`} 
                                className="blog-results-item col-xs-12 col-md-6 col-lg-4 mb-4"
                            >
                                <BlogV2
                                    headerAs='small'
                                    href={post.path?.[0]}
                                    title={post.blog_title?.[0]}
                                    timestamp={post.timestamp}
                                    tags={(post.tags || []).join(',')}
                                    alt={post.thumb_image_alt?.[0]}
                                    src={post.thumb_image_src?.[0]}
                                    dateType="date"
                                    isMainArticle={false}
                                    imageSize="secondary"
                                    categories={getCategories(post)}
                                    onCategoryClick={handleCategoryClick}
                                />
                            </div>
                        ))
                    ) : (
                        !isSearching && <div className="col-12">No articles found</div>
                    )}
                </GridRow>
            </Content>
            
            {/* Loading state and Load More button */}
            {isSearching ? (
                <Row className="position-relative w-100 justify-content-center mb-4">
                    <h4 className="text-muted font-weight-light">LOADING MORE</h4>
                    <Loader spinner={isSearching} top={40} width={4} height={4} variant="primary" />
                </Row>
            ) : (
                !lastPage && (
                    <Row className="position-relative w-100 justify-content-center mb-4">
                        <button className="btn btn-outline-dark" onClick={loadMore}>LOAD MORE</button>
                    </Row>
                )
            )}
        </MainContainer >
    );
}